import React, { useCallback, useRef, useState } from 'react';

import { useDebounceCallback } from '@ovpn-ui/utils';
import { Search as SearchIcon, X } from '@ovpn-ui/icons';
import { IconButton, Input, SearchProps } from '@ovpn-ui/core';

import { SEARCH_DEBOUNCE_MS } from '../../constants/timeouts';

import { IconsContainer, Wrapper } from './ExpandableSearch.styles';

const noop = () => {};

export function ExpandableSearch({ onSearch = noop, value, ...props }: SearchProps) {
    const searchRef = useRef<HTMLInputElement>(null);

    const [searchQuery, setSearchQuery] = useState(value ?? '');
    const [searchVisible, setSearchVisible] = useState(!!searchQuery);

    const searchLazily = useDebounceCallback(
        onSearch,
        props.searchDebounceMs ?? SEARCH_DEBOUNCE_MS,
    );

    const handleSearch = useCallback(
        (searchValue: string, forceClose = false) => {
            setSearchQuery(searchValue);
            searchLazily!(searchValue);

            if (forceClose) {
                setSearchVisible(false);
            } else if (searchValue) {
                setSearchVisible(true);
            }
        },
        [searchLazily],
    );

    const toggleSearchBar = useCallback(() => {
        setSearchVisible(true);
        setTimeout(() => {
            searchRef.current?.focus();
        }, 20);
    }, []);

    const handleBlur = useCallback(() => {
        if (!searchQuery) {
            setSearchVisible(false);
        }
    }, [searchQuery]);

    return (
        <Wrapper $show={searchVisible} className="expandable-search">
            <IconButton
                aria-label="Search"
                variant="ghost"
                data-testid="search-btn"
                className="search-btn"
                onClick={toggleSearchBar}
            >
                <SearchIcon />
            </IconButton>
            <Input
                inputRef={searchRef}
                value={searchQuery}
                onChange={s => handleSearch(s)}
                iconLeft={<SearchIcon />}
                onBlur={handleBlur}
                data-testid="search-input"
                placeholder="Search"
                iconRight={
                    searchQuery ? (
                        <IconsContainer>
                            <X size="lg" onClick={() => handleSearch('', true)} />
                        </IconsContainer>
                    ) : undefined
                }
                {...props}
            />
        </Wrapper>
    );
}
